import {
  DashboardModuleKey,
  DndItem,
  IGridItemConfig,
  IModule,
} from '@app/components/xux-dnd/types/xux-dnd-common.types';
import { I18n } from '@app/i18n/strings-keys';
import { DATE_SOURCE_WITH_ALL_FILTER_OPTIONS } from '@app/shared/constants/data-source.constants';
import {
  ENTITY_TYPES,
  IEventStatus,
  SupportedAppCodes,
} from '@app/shared/types';
import {
  DATA_SOURCE,
  DATA_SOURCE_VALUE,
} from '@app/shared/types/enums/data-source.enum';
import { WIDGET_COMPONENT } from '@app/shared/types/enums/widget-component.enum';
import {
  DASHBOARD_TRANSFER_DATE_MAX,
  EVENT_SELECTOR_SELECT,
  MAX_EVENT_LIMIT,
  WIDGET_FAMILY,
} from '@config/global-constants';
import { XUX_DATEPICKER_PERIODS } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/constants';
import { chain, forEach, get, head, isNil } from 'lodash';
import { BehaviorSubject } from 'rxjs';

const eventsModule = (service): DndItem => ({
  key: DashboardModuleKey.Event,
  title: service.i18nService.instant(I18n.EntityNames.Events),
  permissionChecker: (permissionService) =>
    permissionService.hasPortalPermissions('Events.View'),
  content: {
    schema: {
      type: 'object',
      properties: {
        eventSummary: {
          type: 'object',
        },
      },
    },
    formRules: {
      fields: {
        eventSummary: {
          widget: WIDGET_COMPONENT.event_summary,
          widgetFamily: WIDGET_FAMILY.XUX,
          show: true,
        },
      },
    },
  },
});

const transferModule = (service): DndItem => {
  return {
    key: DashboardModuleKey.Transfers,
    title: service.i18nService.instant(I18n.EntityNames.Transfers),
    permissionChecker: (permissionService) =>
      permissionService.canView(ENTITY_TYPES.TRANSFER) &&
      permissionService.hasPortalPermissions('Events.View', 'Sites.Items.View'),
    content: {
      validation: true,
      schema: {
        type: 'object',
        properties: {
          to_sites: {
            type: 'array',
          },
          source: {
            type: 'string',
          },
          events: {
            type: 'array',
          },
          date: {
            type: 'object',
          },
          transfer_summary: {
            type: 'object',
          },
          defaultData: {
            type: 'object',
          },
        },
      },
      formRules: {
        fields: {
          defaultData: {
            show: false,
          },
          to_sites: {
            title: service.i18nService.instant(I18n.Common.ToSite),
            widget: WIDGET_COMPONENT.select_site_with_grid_modal,
            widgetFamily: WIDGET_FAMILY.XUX,
            selectOptions: service.siteList$,
            class: 'transfer-summary',
            required: true,
            show: true,
            widgetData: {
              dataSource: service.siteList$,
              itemSourceIdKeyPath: 'id',
              isMultiple: true,
              useAllSitesWhenSelectAll: true,
              allSitesMessage: service.i18nService.instant(I18n.Sites.AllSites),
            },
          },
          source: {
            widget: WIDGET_COMPONENT.select,
            widgetFamily: WIDGET_FAMILY.XUX,
            class: 'transfer-summary',
            selectOptions: DATE_SOURCE_WITH_ALL_FILTER_OPTIONS,
            required: true,
            defaultValue: DATA_SOURCE_VALUE.All_Sources,
            show: true,
            title: service.i18nService.instant(I18n.Common.Source),
          },
          events: {
            title: service.i18nService.instant(I18n.EntityNames.Event),
            widget: WIDGET_COMPONENT.select_event_with_grid_modal,
            widgetFamily: WIDGET_FAMILY.XUX,
            class: 'transfer-summary-no-margin',
            selectOptions: service.events$,
            required: true,
            widgetData: {
              availableEventStatus: [IEventStatus.Open, IEventStatus.Closed],
              sortForPortalSelector: [
                'status desc',
                'event_days/start_date_time desc',
                'name asc',
              ],
              isMultiple: true,
              minItems: 1,
              maxItems: MAX_EVENT_LIMIT,
              useInputUrl: false,
              select: EVENT_SELECTOR_SELECT,
              itemSourceIdKeyPath: 'id',
              updateSelectOptionsWhenSingleSelected: true,
            },
            show: (value): boolean => {
              return value.source === DATA_SOURCE.POS;
            },
          },
          date: {
            title: service.i18nService.instant(I18n.Common.Date),
            widget: WIDGET_COMPONENT.xux_datepicker,
            widgetFamily: WIDGET_FAMILY.XUX,
            class: 'transfer-summary-no-margin',
            description: service.i18nService.instant(
              I18n.InfoMessages.DashboardTransferDateMoreThan,
              {
                days: DASHBOARD_TRANSFER_DATE_MAX,
              },
            ),
            required: true,
            widgetData: {
              availableOptions: [XUX_DATEPICKER_PERIODS.CUSTOM],
              maxDateLimit: DASHBOARD_TRANSFER_DATE_MAX,
              resetDateEnd: true,
            },
            show: (value): boolean => value.source !== DATA_SOURCE.POS,
          },
          transfer_summary: {
            widget: WIDGET_COMPONENT.transfer_summary,
            widgetFamily: WIDGET_FAMILY.XUX,
            class: 'transfer-summary-box',
          },
        },
      },
      cacheFormValue: true,
      data: () => {
        const cacheData = service.xuxDndCacheService.getCacheValueByModuleKey(
          DashboardModuleKey.Transfers,
        );
        if (!isNil(cacheData)) {
          return Promise.resolve(cacheData);
        }

        return Promise.resolve({
          source: service.defaultSource,
          to_sites: service.defaultSiteIds,
          date: service.defaultDate,
          defaultData: {
            sites: service.defaultSiteIds,
          },
        });
      },
    },
  };
};

const inventoryValueModule = (service) => {
  return {
    key: DashboardModuleKey.InventoryValue,
    title: service.i18nService.instant(I18n.EntityNames.InventoryValue),
    permissionChecker: (permissionService) =>
      permissionService.hasPortalPermissions('Sites.Items.View') &&
      permissionService.canView(
        ENTITY_TYPES.INVENTORY_ITEM,
        SupportedAppCodes.XDM,
      ),
    content: {
      schema: {
        type: 'object',
        properties: {
          orderSummary: {
            type: 'object',
          },
        },
      },
      formRules: {
        fields: {
          orderSummary: {
            title: '',
            widget: WIDGET_COMPONENT.inventory_value,
            widgetFamily: WIDGET_FAMILY.XUX,
            required: true,
            show: true,
          },
        },
      },
    },
  };
};

const purchaseOrderModule = (service): DndItem => {
  return {
    key: DashboardModuleKey.PurchaseOrders,
    title: service.i18nService.instant(
      I18n.Components.LandingPageComponent.PurchaseOrders,
    ),
    permissionChecker: (permissionService) =>
      permissionService.canView(ENTITY_TYPES.ORDER),
    content: {
      schema: {
        type: 'object',
        properties: {
          orderSummary: {
            type: 'object',
          },
        },
      },
      formRules: {
        fields: {
          orderSummary: {
            widget: WIDGET_COMPONENT.purchase_order_summary,
            widgetFamily: WIDGET_FAMILY.XUX,
            show: true,
          },
        },
      },
    },
  };
};

const standWorksheetModule = (service): DndItem => {
  const autoRefreshObs: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );
  const autoRefreshInterval: number = 30; // in seconds

  return {
    key: DashboardModuleKey.StandWorksheet,
    title: service.i18nService.instant(I18n.EntityNames.StandWorksheets),
    permissionChecker: (permissionService) =>
      permissionService.canView(ENTITY_TYPES.STAND_WORKSHEET) &&
      permissionService.hasPortalPermissions('Events.View'),
    content: {
      validation: true,
      schema: {
        type: 'object',
        properties: {
          eventId: {
            type: 'object',
          },
          stand_worksheet: {
            type: 'object',
          },
        },
      },
      formRules: {
        fields: {
          defaultData: {
            show: false,
          },
          eventId: {
            title: service.i18nService.instant(I18n.EntityNames.Event),
            widget: WIDGET_COMPONENT.select_event_with_grid_modal,
            widgetFamily: WIDGET_FAMILY.XUX,
            selectOptions: service.events$,
            required: true,
            widgetData: {
              availableEventStatus: [IEventStatus.Open, IEventStatus.Closed],
              sortForPortalSelector: [
                'status desc',
                'event_days/start_date_time desc',
                'name asc',
              ],
              isMultiple: false,
              useInputUrl: false,
              select: EVENT_SELECTOR_SELECT,
              itemSourceIdKeyPath: 'id',
              modalIsHidden$: autoRefreshObs,
              updateSelectOptionsWhenSingleSelected: true,
            },
          },
          stand_worksheet: {
            widget: WIDGET_COMPONENT.stand_worksheet_summary,
            widgetFamily: WIDGET_FAMILY.XUX,
            class: 'stand-worksheet-summary-box',
            widgetData: {
              autoRefresh: {
                interval: autoRefreshInterval,
                autoRefreshObs,
              },
            },
          },
        },
      },
      autoRefresh: {
        enabled: true,
        interval: autoRefreshInterval,
        autoRefreshObs,
      },
      cacheFormValue: true,
      data: () => {
        const cacheData = service.xuxDndCacheService.getCacheValueByModuleKey(
          DashboardModuleKey.StandWorksheet,
        );
        if (!isNil(cacheData)) {
          return Promise.resolve(cacheData);
        }

        return Promise.resolve({
          eventId: [get(head(service.defaultEvents), 'id')],
        });
      },
    },
  };
};

const getTotalModules = (service): IModule => ({
  [DashboardModuleKey.Event]: eventsModule(service),
  [DashboardModuleKey.Transfers]: transferModule(service),
  [DashboardModuleKey.InventoryValue]: inventoryValueModule(service),
  [DashboardModuleKey.PurchaseOrders]: purchaseOrderModule(service),
  [DashboardModuleKey.StandWorksheet]: standWorksheetModule(service),
});

const defaultConfig: IGridItemConfig[][] = [
  [
    {
      cols: 1,
      rows: 29,
      key: DashboardModuleKey.Event,
    },
    {
      cols: 1,
      rows: 24,
      key: DashboardModuleKey.StandWorksheet,
    },
  ],
  [
    {
      cols: 1,
      rows: 36,
      key: DashboardModuleKey.Transfers,
    },
    {
      cols: 1,
      rows: 11,
      key: DashboardModuleKey.InventoryValue,
      class: 'inventory-value-panel',
    },
    {
      cols: 1,
      rows: 20,
      key: DashboardModuleKey.PurchaseOrders,
    },
  ],
];

const defaultItemConfig = (service): IGridItemConfig[] => {
  const gridItemConfig = [];

  const calcXY = (configColumn: IGridItemConfig[], colIndex: number): void => {
    forEach(configColumn, (config: IGridItemConfig, index: number) => {
      gridItemConfig.push({
        ...config,
        itemConfig: getTotalModules(service)[config.key],
        x: colIndex,
        y: chain(configColumn)
          .slice(0, index)
          .sumBy('rows')
          .valueOf() as number,
      });
    });
  };
  forEach(defaultConfig, (column: IGridItemConfig[], index: number) => {
    calcXY(column, index);
  });

  return gridItemConfig;
};

export {
  eventsModule,
  transferModule,
  inventoryValueModule,
  purchaseOrderModule,
  getTotalModules,
  defaultItemConfig,
};
